import { fetchAuthSession, signIn, signOut } from "aws-amplify/auth";
import gql from "graphql-tag";
import mixpanel from "mixpanel-browser";

import { configureUserPool } from "./amplifyConfig";

// https://runelabs.atlassian.net/browse/SW-2470
/* eslint-disable  @typescript-eslint/no-explicit-any */
export interface AuthProviderOptions {
  graphqlClient: any;
}

export class AuthProvider {
  private graphqlClient: any;
  public currentUser: any;

  public constructor(options: AuthProviderOptions) {
    this.graphqlClient = options.graphqlClient;
    this.currentUser = null;
  }

  public login = ({
    username,
    password,
    clientMetadata
  }: Record<string, unknown>): Promise<unknown> => {
    mixpanel.track("signIn", { username: username });

    // Reconfigure explicitly rather than trusting the boot-time choice. A sticky
    // SSO flag from an abandoned Duo attempt in this tab would otherwise send
    // this sign-in to the wrong user pool, where it fails as a bad password.
    configureUserPool();

    return signIn({
      username: username as string,
      password: password as string,
      options: { clientMetadata: clientMetadata as Record<string, string> }
    });
  };

  public logout = (): Promise<any> => {
    mixpanel.track("logout");
    this.currentUser = null;
    return signOut();
  };

  public checkAuth = async (): Promise<void> => {
    // fetchAuthSession resolves with `tokens === undefined` when there is no
    // session rather than rejecting, so the absent-token case has to be checked
    // as well as the throw — otherwise requireAuth admits a logged-out user.
    const session = await fetchAuthSession().catch(() => null);

    if (!session?.tokens?.accessToken) {
      this.currentUser = null;
      // If there is no current session. Return a rejected promise,
      // and do not return an error message. This will prevent an
      // error message being displayed when a user first navigates
      // to the login page, but has not yet signed in.
      return Promise.reject({ message: false });
    }
  };

  public checkError = (): Promise<void> => {
    return Promise.resolve();
  };

  public getPermissions = (): Promise<string[]> => {
    return Promise.resolve([]);
  };

  public getIdentity = async (): Promise<any> => {
    if (this.currentUser) {
      return this.currentUser;
    }

    const { data } = await this.graphqlClient.query({
      query: gql`
        query getUser($cursor: Cursor) {
          user {
            id
            name
            username
            email
            created
            defaultMembership {
              role {
                admin
                displayName
                superAdmin
                canSeePHI
              }
              org {
                id
                displayName
              }
            }
            membershipList(cursor: $cursor, limit: 50) {
              memberships {
                id
                org {
                  id
                  displayName
                }
              }
            }
          }
        }
      `,
      variables: {
        cursor: null
      }
    });
    this.currentUser = data.user;
    return this.currentUser;
  };
}
